/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Link,
  ThemeProvider,
  InputAdornment,
  IconButton,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomToolBar from "../../components/custom-toolbar";
import CustomFooter from "../../components/custom-footer";
import CustomVCode from "../../components/custom-vcode";
import CustomDialog from "../../components/custom-dialog";
import { Stack } from "@mui/system";
import { useForm, useWatch } from "react-hook-form";
import { useImmer } from "use-immer";
import { useCookies } from "react-cookie";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import useAuth from "../../hooks/use-auth";
import useCompany from "../../hooks/use-company";
import useApi from "../../hooks/use-api";
import theme from "../../theme";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { isEmpty } from "lodash";

// const companyDefId = { sanchongzhiye: "22" };

const companyDefId = { 
  sanchong: "22", 
  "temp-staging-sanchong": "22"
};

const Login = () => {
  // Data
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm();
  const [state, produce] = useImmer({
    vcode: "",
    dialogMsg: "",
    showPwd: false,
    isLoading: false,
    paths: [
      { title: "首頁", link: "/login" },
      { title: "會員登入", link: "/login" },
    ],
  });
  const { vcode, dialogMsg, showPwd, isLoading, paths } = state;
  const [creCareToken, setCreCareToken] = useCookies(["creCareToken"]);
  const { setAuthToken, signOut } = useAuth();
  const { postCRECareLogin, postCRELogin } = useApi();
  const { setEDepartmentId, companyInfo } = useCompany();
  const navigate = useNavigate();
  const { eDepartmentId } = useParams();

  //CSS
  const card = css({
    width: "min(320px, 100vw - 40px)",
    padding: "50px 102px",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.25)",
    margin: "auto",
    "@media(max-width: 576px)": {
      padding: "10px 15px",
    },
  });
  const titleHint = css({ color: "#2b2b2b" });
  const textInput = css({ width: "100%", backgroundColor: "#f5f5f5" });
  const sendBtn = css({ padding: "10px 44px", backgroundColor: "#009043" });

  //Method
  const onSubmit = (datas) => {
    produce((draft) => void (draft.isLoading = true));
    const params = { userId: datas.account, password: datas.password };
    //客戶關懷登入
    postCRECareLogin({
      params,
      onSuccess: (result) => {
        if (result.isSuccess === false) {
          produce((draft) => {
            draft.dialogMsg = result.message;
          });
          closeLoading();
        } else {
          const creToken = result.result.jwtToken;
          const expires = moment().add("1", "d").toDate();
          setCreCareToken("cres_token", creToken, { expires });
          postCRELoginApi(creToken);
        }
      },
      onFail: (result) => {
        console.log("[Test] onFail:", result);
        produce((draft) => {
          draft.dialogMsg = "帳號密碼錯誤，請洽您的專員";
        });
        closeLoading();
      },
    });
  };
  // 線上選定的登入
  const postCRELoginApi = (creToken) => {
    const params = { creToken, depId: companyDefId[eDepartmentId] /* eDepartmentId */ };
    postCRELogin({
      params,
      onSuccess: (result) => {
        setAuthToken(result.cresJwtToken);
        if (result.payInfo.length === 0) {
          produce((draft) => void (draft.dialogMsg = "查無資訊，請洽您的專員"));
          signOut()
        } else {
          navigate(`/${eDepartmentId}/home`);
        }
        closeLoading();
      },
      onFail: (result) => {
        console.log("[Test] CRELogin onFail:", result);
        closeLoading();
      },
    });
  };
  const handleDialogClose = () => {
    produce((draft) => void (draft.dialogMsg = ""));
  };
  const changeCode = (value) => {
    produce((draft) => void (draft.vcode = value));
  };
  const closeLoading = () => {
    produce((draft) => void (draft.isLoading = false));
  };

  const account = useWatch({ control, name: "account" });

  useEffect(() => {
    !!eDepartmentId && setEDepartmentId(eDepartmentId);
  }, [eDepartmentId]);

  useEffect(() => {
    if (isEmpty(companyInfo || eDepartmentId)) {
      return navigate("/no-data");
    }
    !!companyInfo &&
      produce(
        (draft) =>
          void (draft.paths = [
            { title: "首頁", link: `/${eDepartmentId}/login` },
            {
              title: companyInfo.name + "會員專區",
              link: `/${eDepartmentId}/login`,
            },
            { title: "會員登入", link: `/${eDepartmentId}/login` },
          ])
      );
  }, [companyInfo]);

  return (
    <ThemeProvider theme={theme}>
      <CustomToolBar title={"會員登入"} paths={paths} />
      <Box sx={{ flex: 1 }}>
        <Box css={card}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2} alignItems="center">
              <h4 css={titleHint}>請輸入您的帳號密碼</h4>
              <TextField
                {...register("account")}
                label="帳號："
                variant="outlined"
                css={textInput}
                size="small"
              />
              <TextField
                {...register("password")}
                label="密碼："
                variant="outlined"
                css={textInput}
                type={showPwd ? "text" : "password"}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          produce((draft) => void (draft.showPwd = !showPwd))
                        }
                      >
                        {showPwd ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                {...register("code", {
                  validate: { vcode: (v) => v === vcode },
                })}
                label="驗證碼："
                variant="outlined"
                placeholder="請依照大小寫輸入"
                css={textInput}
                size="small"
                InputProps={{
                  endAdornment: <CustomVCode onValueChange={changeCode} />,
                }}
                error={errors.code ? true : false}
                helperText={errors.code?.type === "vcode" ? "驗證碼錯誤" : ""}
              />
              <LoadingButton
                variant="contained"
                css={sendBtn}
                type="submit"
                loading={isLoading}
              >
                確認送出
              </LoadingButton>
              <Link
                underline="hover"
                color="green"
                href={
                  !!account
                    ? `/${eDepartmentId}/forget?acc=${account}`
                    : `/${eDepartmentId}/forget`
                }
              >
                忘記密碼
              </Link>
            </Stack>
          </form>
        </Box>
      </Box>
      <CustomDialog
        isOpen={!!dialogMsg && dialogMsg.length > 0}
        message={dialogMsg}
        onConfirm={handleDialogClose}
        onClose={handleDialogClose}
      />
      <CustomFooter />
    </ThemeProvider>
  );
};
export default Login;
